import { Badge, Table } from "flowbite-react";
import {
  formatDateToDDMM,
  formatDateToYY,
  formatToReal,
  getCompanyAge,
  getImageURLScore,
  getLetterScore,
  getScoreLegends,
  maskCEP,
  parseFoundationDate,
} from "../../utils/utils";
import TitleStripe from "../common/TitleStripe";
import NoOcurrence from "../common/query/NoOcurrence";
import Protestos from "../common/query/Protestos";
import ChequesSemFundos from "../common/query/ChequesSemFundos";
import ChequesSustados from "../common/query/ChequesSustados";
import PendenciasFinanceiras from "../common/query/PendenciasFinanceiras";
import AcoesCiveis from "../common/query/AcoesCiveis";
import FalenciaRecuperacaoJudicial from "../common/query/FalenciaRecuperacaoJudicial";
import QuadroSocietario from "../common/query/QuadroSocietario";
import HistoricoConsultas from "../common/query/HistoricoConsultas";
import Score from "../common/query/Score";
import Administrador from "../common/query/Administrador";
import ScoreRating from "./custom-queries-credit/ScoreRating";
import DecisionBanner from "./DecisionBanner";
import moment from "moment";
import ModalReceitaFederal from "../modals/ModalReceitaFederal";

const DefineLimiteQuery = ({ data, dataFull }) => {
  const probRaw = data?.score?.score_pj?.probabilidade;
  const prob = typeof probRaw === "number" ? probRaw : null;

  let riskLabel = "Indefinido";
  let riskClasses = {
    chip: "bg-gray-100 text-gray-700",
    ring: "from-slate-50 to-slate-100",
    dotActive: "bg-gray-300",
    dotIdle: "bg-gray-100",
  };

  if (prob !== null) {
    if (prob < 10) {
      riskLabel = "Baixo";
      riskClasses = {
        chip: "bg-emerald-100 text-emerald-700",
        ring: "from-emerald-50 to-teal-50",
        dotActive: "bg-emerald-500",
        dotIdle: "bg-emerald-100",
      };
    } else if (prob < 25) {
      riskLabel = "Moderado";
      riskClasses = {
        chip: "bg-amber-100 text-amber-700",
        ring: "from-amber-50 to-yellow-50",
        dotActive: "bg-amber-500",
        dotIdle: "bg-amber-100",
      };
    } else if (prob < 50) {
      riskLabel = "Alto";
      riskClasses = {
        chip: "bg-orange-100 text-orange-700",
        ring: "from-orange-50 to-amber-50",
        dotActive: "bg-orange-500",
        dotIdle: "bg-orange-100",
      };
    } else {
      riskLabel = "Altíssimo";
      riskClasses = {
        chip: "bg-rose-100 text-rose-700",
        ring: "from-rose-50 to-red-50",
        dotActive: "bg-rose-500",
        dotIdle: "bg-rose-100",
      };
    }
  }
  return (
    <div>
      <DecisionBanner compact={true} flag={data?.decisao?.flag} />

      <TitleStripe>Dados Cadastrais</TitleStripe>

      <div className="mt-4 grid grid-cols-2 gap-6 bg-white border border-gray-200 rounded-xl p-6 shadow-sm text-sm">
        <div className="space-y-4">
          <div>
            <div className="text-gray-500 text-xs uppercase">Razão Social</div>
            <div className="font-medium text-gray-900">
              {data?.identificacao_completo?.razao_social || "Indisponível"}
            </div>
            <ModalReceitaFederal cnpj={data?.identificacao_completo?.cnpj} />
          </div>

          <div>
            <div className="text-gray-500 text-xs uppercase">Nome Fantasia</div>
            <div className="font-medium text-gray-900">
              {data?.identificacao_completo?.nome_fantasia || "Indisponível"}
            </div>
          </div>

          <div>
            <div className="text-gray-500 text-xs uppercase">
              Natureza Jurídica
            </div>
            <div className="font-medium text-gray-900">
              {data?.identificacao_completo?.natureza_juridica ||
                "Indisponível"}
            </div>
          </div>

          <div>
            <div className="text-gray-500 text-xs uppercase">CNPJ/MF</div>
            <div className="font-medium text-gray-900">
              {data?.identificacao_completo?.cnpj || "Indisponível"}
            </div>
          </div>

          <div>
            <div className="text-gray-500 text-xs uppercase">
              Inscrição Estadual
            </div>
            <div className="font-medium text-gray-900">
              {data?.identificacao_completo?.inscricao_estadual ||
                "Indisponível"}
            </div>
          </div>

          <div>
            <div className="text-gray-500 text-xs uppercase">Segmento</div>
            <div className="font-medium text-gray-900">
              {data?.identificacao_completo?.segmento || "Indisponível"}
            </div>
          </div>

          <div>
            <div className="text-gray-500 text-xs uppercase">
              CNAE Principal
            </div>
            <div className="font-medium text-gray-900">
              {data?.identificacao_completo?.ramo_atividade_primario
                ?.atividade || "Indisponível"}
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <div className="text-gray-500 text-xs uppercase">
              Situação do CNPJ
            </div>
            <div
              className={`inline-block px-2 py-1 text-xs font-semibold rounded-md text-white ${
                data?.identificacao_completo?.situacao_cnpj?.toLowerCase() ===
                "ativo"
                  ? "bg-green-600"
                  : "bg-red-600"
              }`}
            >
              {data?.identificacao_completo?.situacao_cnpj || "Indisponível"}
            </div>
          </div>

          <div>
            <div className="text-gray-500 text-xs uppercase">Endereço</div>
            <div className="font-medium text-gray-900">
              {data?.localizacao_completo?.matriz?.endereco_matriz ||
                "Indisponível"}
            </div>
          </div>

          <div>
            <div className="text-gray-500 text-xs uppercase">Bairro</div>
            <div className="font-medium text-gray-900">
              {data?.localizacao_completo?.matriz?.bairro_matriz ||
                "Indisponível"}
            </div>
          </div>

          <div>
            <div className="text-gray-500 text-xs uppercase">Cidade/UF</div>
            <div className="font-medium text-gray-900">
              {data?.localizacao_completo?.matriz?.cidade_matriz ||
                "Indisponível"}{" "}
              / {data?.localizacao_completo?.matriz?.uf_matriz || "--"}
            </div>
          </div>

          <div>
            <div className="text-gray-500 text-xs uppercase">CEP</div>
            <div className="font-medium text-gray-900">
              {maskCEP(data?.localizacao_completo?.matriz?.cep_matriz) ||
                "Indisponível"}
            </div>
          </div>
          {(() => {
            const fundacaoStr = data?.identificacao_completo?.data_fundacao;
            const fundacao = parseFoundationDate(fundacaoStr);
            const age = getCompanyAge(fundacao);

            return (
              <div>
                <div className="text-gray-500 text-xs uppercase">Fundação</div>
                <div className="flex items-center gap-2">
                  <div className="font-medium text-gray-900">
                    {fundacao ? fundacao.format("DD/MM/YYYY") : "Indisponível"}
                  </div>

                  <Badge className="text-xs text-gray-500 w-fit">
                    {fundacao && age
                      ? `${age.years} ano${age.years !== 1 ? "s" : ""}${
                          age.months
                            ? ` e ${age.months} mes${
                                age.months !== 1 ? "es" : ""
                              }`
                            : ""
                        }`
                      : "—"}
                  </Badge>
                </div>
              </div>
            );
          })()}

          <div>
            <div className="text-gray-500 text-xs uppercase">
              Capital Social
            </div>
            <div className="font-medium text-gray-900">
              {formatToReal(data?.identificacao_completo?.capital_inicial) ||
                "Indisponível"}
            </div>
          </div>
        </div>
      </div>

      <TitleStripe>Avaliação Preliminar de Crédito</TitleStripe>

      <div className="mt-4 bg-white border border-gray-200 rounded-xl p-6 shadow-sm text-sm space-y-6">
        <div className="grid grid-cols-2 gap-3">
          <div className="relative rounded-xl overflow-hidden bg-white shadow-sm">
            {/* fundo sutil */}
            <div className="absolute inset-0 bg-gradient-to-br from-amber-50 to-orange-50" />
            <div className="relative p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[11px] uppercase tracking-wide text-gray-500">
                  Faturamento Presumido
                </div>
              </div>

              <div className="text-xl font-extrabold text-gray-900">
                {
                  // se tiver formatToReal e for número, formata. senão, usa o valor como veio
                  typeof data?.faturamento_presumido?.faturamento_anual ===
                  "number"
                    ? formatToReal(data.faturamento_presumido.faturamento_anual)
                    : data?.faturamento_presumido?.faturamento_anual ||
                      "Indisponível"
                }
              </div>

              {data?.faturamento_presumido?.mensagem && (
                <div
                  className="mt-2 text-[11px] text-gray-600 bg-white/60 rounded-lg p-2"
                  style={{ lineHeight: "1.1" }}
                >
                  {data.faturamento_presumido.mensagem}
                </div>
              )}
            </div>
          </div>

          {/* === Limite de Crédito === */}
          <div className="relative rounded-2xl overflow-hidden bg-white shadow-sm">
            {/* fundo sutil */}
            <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-indigo-50" />
            <div className="relative p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[11px] uppercase tracking-wide text-gray-500">
                  Limite de Crédito
                </div>
              </div>

              <div className="text-xl font-extrabold text-gray-900">
                {data?.limite_credito?.score
                  ? formatToReal(data.limite_credito.score)
                  : "Indisponível"}
              </div>

              {/* linha auxiliar opcional */}
              <div className="mt-1 text-[12px] text-gray-500">
                {data?.limite_credito?.score ? (
                  <div
                    className="mt-2 text-[11px] text-gray-600 bg-white/60 rounded-lg p-2"
                    style={{ lineHeight: "1.1" }}
                  >
                    Limite de crédito estimado a partir de informações
                    comportamentais e cadastrais da empresa.
                  </div>
                ) : (
                  "Aguardando atualização"
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Score de Risco */}
        <div>
          <div className="text-gray-500 text-xs uppercase mb-3">
            Score de Risco
          </div>
          {data?.score?.score_pj?.score == 2 ? (
            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-yellow-800 text-sm">
              <p className="font-medium">
                Não foi possível calcular o score desta empresa.
              </p>
              <p className="mt-1">
                É necessário verificar as outras informações disponíveis no
                relatório para uma análise completa.
              </p>
            </div>
          ) : (
            <div className="flex  flex-row items-center gap-6">
              <div className="flex flex-col items-center md:w-1/3">
                <Score score={+data?.score?.score_pj?.score} />
              </div>

              <div className="w-2/3 relative rounded-2xl overflow-hidden shadow-sm border border-gray-200">
                {/* fundo sutil */}
                <div
                  className={`absolute inset-0 bg-gradient-to-br ${riskClasses.ring}`}
                />

                <div className="relative p-4">
                  {/* header */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">📉</span>
                      <div className="text-sm font-semibold text-gray-700">
                        Probabilidade de Inadimplência
                      </div>
                    </div>

                    {/* badges à direita */}
                    <div className="flex items-center gap-2">
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full font-semibold ${riskClasses.chip}`}
                      >
                        {riskLabel}
                      </span>
                      <span className="text-sm px-2 py-0.5 rounded-full bg-cyan-100 text-cyan-700 font-semibold">
                        {prob !== null ? `${prob}%` : "--"}
                      </span>
                    </div>
                  </div>

                  {/* escala de risco com 4 pontos (não é barra de progresso) */}
                  <div className="flex items-center gap-2 mb-3">
                    {[0, 1, 2, 3].map((i) => {
                      const thresholds = [10, 25, 50, 100];
                      const active =
                        prob !== null ? prob >= thresholds[i] : false;
                      return (
                        <div
                          key={i}
                          className={`h-2 w-full rounded-full transition-colors ${
                            active ? riskClasses.dotActive : riskClasses.dotIdle
                          }`}
                        />
                      );
                    })}
                  </div>

                  {/* texto explicativo */}
                  <div className="text-sm text-gray-700 leading-relaxed bg-white/70 rounded-lg p-3">
                    {data?.score?.score_pj?.texto ? (
                      <>{data.score.score_pj.texto}</>
                    ) : (
                      <span className="italic text-gray-400">
                        Sem explicação adicional.
                      </span>
                    )}
                  </div>

                  {/* rodapé opcional */}
                  <div className="mt-3 text-[11px] text-gray-500 flex items-center justify-between">
                    <span>Modelo: Score PJ</span>
                    {/* se tiver timestamp de geração, pode mostrar aqui */}
                    {/* <span>Atualizado em {formatDate(data.score.score_pj.updatedAt)}</span> */}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Alerta Legal */}
        <div className="text-xs text-gray-600 border-t pt-4">
          <span className="font-semibold text-gray-700">Importante:</span> a
          decisão de aprovação ou não do crédito é de exclusiva responsabilidade
          do concedente. As informações prestadas pelo provedor têm como
          objetivo subsidiar essas decisões e, em hipótese alguma, devem ser
          utilizadas como justificativa única para concessão ou recusa de
          crédito.
        </div>
      </div>

      {dataFull?.scoreRating?.statusRetorno == 1 && (
        <ScoreRating score={dataFull?.scoreRating} block={false} />
      )}

      <div>
        <TitleStripe className="mb-5">Quadro Societário</TitleStripe>
        {data && !data?.socios?.mensagem ? (
          <QuadroSocietario
            socios={
              Array.isArray(data?.socios?.socio)
                ? data?.socios?.socio
                : [...[], data?.socios?.socio]
            }
          />
        ) : (
          <NoOcurrence />
        )}
      </div>

      <div>
        <TitleStripe className="mb-5">Administradores</TitleStripe>
        {data && !data?.administradores?.mensagem ? (
          <Administrador
            administradores={
              Array.isArray(data?.administradores?.administrador)
                ? data?.administradores?.administrador
                : [...[], data?.administradores?.administrador]
            }
          />
        ) : (
          <NoOcurrence />
        )}
      </div>

      <div>
        <TitleStripe className="mb-5">
          Participações dos Sócios e Administradores em Outras Empresas
        </TitleStripe>
        {!data?.participacoes_socios_adm_outras_empresas?.mensagem ? (
          <>
            {Array.isArray(
              data?.participacoes_socios_adm_outras_empresas?.socio
            ) ? (
              <>
                {data?.participacoes_socios_adm_outras_empresas?.socio?.map(
                  (socioP, index) => (
                    <div key={index} className="mt-10">
                      <div className="font-semibold mb-1 text-blue-600 underline">
                        {socioP.nome_socio}
                      </div>

                      <div className="overflow-x-auto mt-3 mb-5 rounded-lg border border-gray-300">
                        <Table striped>
                          <Table.Head className="text-black">
                            <Table.HeadCell
                              className="text-center py-2 px-1"
                              style={{ background: "#EBF5FF" }}
                            >
                              Nº
                            </Table.HeadCell>
                            <Table.HeadCell
                              className="py-2"
                              style={{ background: "#EBF5FF" }}
                            >
                              Denominação Social
                            </Table.HeadCell>
                            <Table.HeadCell
                              className="text-center py-2 px-1"
                              style={{ background: "#EBF5FF" }}
                            >
                              Data <br /> Entrada
                            </Table.HeadCell>
                            <Table.HeadCell
                              className="text-center py-2 px-1"
                              style={{ background: "#EBF5FF" }}
                            >
                              Part. (%)
                            </Table.HeadCell>
                            <Table.HeadCell
                              className="text-center py-2 px-1"
                              style={{ background: "#EBF5FF" }}
                            >
                              Situação
                            </Table.HeadCell>
                            <Table.HeadCell
                              className="py-2"
                              style={{ background: "#EBF5FF" }}
                            >
                              Pendências
                            </Table.HeadCell>
                          </Table.Head>

                          <Table.Body className="divide-y">
                            {Array.isArray(socioP?.socios_participantes) ? (
                              <>
                                {socioP?.socios_participantes?.map(
                                  (socio, index) => (
                                    <Table.Row
                                      key={index}
                                      className="bg-white dark:border-gray-700 dark:bg-gray-800 text-black"
                                    >
                                      <Table.Cell className="font-medium dark:text-white text-center py-1 px-1">
                                        {index + 1}
                                      </Table.Cell>
                                      <Table.Cell className=" py-1 w-96">
                                        <div>{socio.razao_social}</div>
                                        <div style={{ fontSize: "12px" }}>
                                          CNPJ: {socio.cnpj}
                                        </div>
                                      </Table.Cell>
                                      <Table.Cell className="text-center py-1 px-1">
                                        {socio.entrada}
                                      </Table.Cell>
                                      <Table.Cell className="text-center py-1 px-1 px-1">
                                        {socio.participacao}
                                      </Table.Cell>
                                      <Table.Cell className="text-center py-1 w-24">
                                        {socio.situacao_cnpj}
                                      </Table.Cell>
                                      <Table.Cell className="py-1 text-2xs text-black">
                                        {socio?.indicador_debito?.toUpperCase() ===
                                        "SIM" ? (
                                          <>
                                            {socio?.restricoes_documento
                                              ?.cheques_sem_fundo
                                              ?.quantidade !== "0" && (
                                              <div className="uppercase">
                                                Cheques Sem Fundos:{" "}
                                                <span className="text-red-500 font-semibold">
                                                  {
                                                    socio?.restricoes_documento
                                                      ?.cheques_sem_fundo
                                                      ?.quantidade
                                                  }{" "}
                                                  ocorrências
                                                </span>
                                              </div>
                                            )}

                                            {socio?.restricoes_documento
                                              ?.protestos?.quantidade !==
                                              "0" && (
                                              <div className="uppercase">
                                                Protestos:{" "}
                                                <span className="text-red-500 font-semibold">
                                                  {formatToReal(
                                                    socio?.restricoes_documento
                                                      ?.protestos?.valor
                                                  )}
                                                </span>
                                              </div>
                                            )}

                                            {socio?.restricoes_documento
                                              ?.pendencias_restricoes
                                              ?.quantidade !== "0" && (
                                              <div className="uppercase">
                                                Pendências:{" "}
                                                <span className="text-red-500 font-semibold">
                                                  {formatToReal(
                                                    socio?.restricoes_documento
                                                      ?.pendencias_restricoes
                                                      ?.valor
                                                  )}
                                                </span>
                                              </div>
                                            )}

                                            {socio?.restricoes_documento
                                              ?.acoes_judiciais?.quantidade !==
                                              "0" && (
                                              <div className="uppercase">
                                                Ações cíveis:{" "}
                                                <span className="text-red-500 font-semibold">
                                                  {formatToReal(
                                                    socio?.restricoes_documento
                                                      ?.acoes_judiciais?.valor
                                                  )}
                                                </span>
                                              </div>
                                            )}

                                            {socio?.restricoes_documento
                                              ?.falencias_recuperacao_judicial
                                              ?.quantidade !== "0" && (
                                              <div className="uppercase">
                                                Rec. Judicial/Falências:{" "}
                                                <span className="text-red-500 font-semibold">
                                                  {formatToReal(
                                                    socio?.restricoes_documento
                                                      ?.falencias_recuperacao_judicial
                                                      ?.valor
                                                  )}
                                                </span>
                                              </div>
                                            )}
                                          </>
                                        ) : (
                                          <div className="text-blue-500">
                                            Não há débitos
                                          </div>
                                        )}
                                      </Table.Cell>
                                    </Table.Row>
                                  )
                                )}
                              </>
                            ) : (
                              <>
                                <Table.Row className="bg-white dark:border-gray-700 dark:bg-gray-800 text-black">
                                  <Table.Cell className="font-medium dark:text-white text-center py-1 px-1">
                                    {1}
                                  </Table.Cell>
                                  <Table.Cell className=" py-1 w-96">
                                    <div>
                                      {
                                        socioP?.socios_participantes
                                          ?.razao_social
                                      }
                                    </div>
                                    <div style={{ fontSize: "12px" }}>
                                      CNPJ: {socioP?.socios_participantes?.cnpj}
                                    </div>
                                  </Table.Cell>
                                  <Table.Cell className="text-center py-1 px-1">
                                    {socioP?.socios_participantes?.entrada}
                                  </Table.Cell>
                                  <Table.Cell className="text-center py-1 px-1 px-1">
                                    {socioP?.socios_participantes?.participacao}
                                  </Table.Cell>
                                  <Table.Cell className="text-center py-1 w-24">
                                    {
                                      socioP?.socios_participantes
                                        ?.situacao_cnpj
                                    }
                                  </Table.Cell>
                                  <Table.Cell className="py-1 text-2xs text-black">
                                    {socioP?.socios_participantes?.indicador_debito?.toUpperCase() ===
                                    "SIM" ? (
                                      <>
                                        {socioP?.socios_participantes
                                          ?.restricoes_documento
                                          ?.cheques_sem_fundo?.quantidade !==
                                          "0" && (
                                          <div className="uppercase">
                                            Cheques Sem Fundos:{" "}
                                            <span className="text-red-500 font-semibold">
                                              {
                                                socioP?.socios_participantes
                                                  ?.restricoes_documento
                                                  ?.cheques_sem_fundo
                                                  ?.quantidade
                                              }{" "}
                                              ocorrências
                                            </span>
                                          </div>
                                        )}

                                        {socioP?.socios_participantes
                                          ?.restricoes_documento?.protestos
                                          ?.quantidade !== "0" && (
                                          <div className="uppercase">
                                            Protestos:{" "}
                                            <span className="text-red-500 font-semibold">
                                              {formatToReal(
                                                socioP?.socios_participantes
                                                  ?.restricoes_documento
                                                  ?.protestos?.valor
                                              )}
                                            </span>
                                          </div>
                                        )}

                                        {socioP?.socios_participantes
                                          ?.restricoes_documento
                                          ?.pendencias_restricoes
                                          ?.quantidade !== "0" && (
                                          <div className="uppercase">
                                            Pendências:{" "}
                                            <span className="text-red-500 font-semibold">
                                              {formatToReal(
                                                socioP?.socios_participantes
                                                  ?.restricoes_documento
                                                  ?.pendencias_restricoes?.valor
                                              )}
                                            </span>
                                          </div>
                                        )}

                                        {socioP?.socios_participantes
                                          ?.restricoes_documento
                                          ?.acoes_judiciais?.quantidade !==
                                          "0" && (
                                          <div className="uppercase">
                                            Ações cíveis:{" "}
                                            <span className="text-red-500 font-semibold">
                                              {formatToReal(
                                                socioP?.socios_participantes
                                                  ?.restricoes_documento
                                                  ?.acoes_judiciais?.valor
                                              )}
                                            </span>
                                          </div>
                                        )}

                                        {socioP?.socios_participantes
                                          ?.restricoes_documento
                                          ?.falencias_recuperacao_judicial
                                          ?.quantidade !== "0" && (
                                          <div className="uppercase">
                                            Rec. Judicial/Falências:{" "}
                                            <span className="text-red-500 font-semibold">
                                              {formatToReal(
                                                socioP?.socios_participantes
                                                  ?.restricoes_documento
                                                  ?.falencias_recuperacao_judicial
                                                  ?.valor
                                              )}
                                            </span>
                                          </div>
                                        )}
                                      </>
                                    ) : (
                                      <div className="text-blue-500">
                                        Não há débitos
                                      </div>
                                    )}
                                  </Table.Cell>
                                </Table.Row>
                              </>
                            )}
                          </Table.Body>
                        </Table>
                      </div>
                    </div>
                  )
                )}
              </>
            ) : (
              <>
                <div className="mt-10">
                  <div className="font-semibold mb-1 text-blue-600 text-center underline">
                    {
                      data?.participacoes_socios_adm_outras_empresas?.socio
                        .nome_socio
                    }
                  </div>

                  <div className="overflow-x-auto mt-3 mb-5 rounded-lg border border-gray-300">
                    <Table striped>
                      <Table.Head className="text-black">
                        <Table.HeadCell
                          className="text-center py-2 px-1"
                          style={{ background: "#EBF5FF" }}
                        >
                          Nº
                        </Table.HeadCell>
                        <Table.HeadCell
                          className="py-2"
                          style={{ background: "#EBF5FF" }}
                        >
                          Denominação Social
                        </Table.HeadCell>
                        <Table.HeadCell
                          className="text-center py-2 px-1"
                          style={{ background: "#EBF5FF" }}
                        >
                          Data <br /> Entrada
                        </Table.HeadCell>
                        <Table.HeadCell
                          className="text-center py-2 px-1"
                          style={{ background: "#EBF5FF" }}
                        >
                          Part. (%)
                        </Table.HeadCell>
                        <Table.HeadCell
                          className="text-center py-2 px-1"
                          style={{ background: "#EBF5FF" }}
                        >
                          Situação
                        </Table.HeadCell>
                        <Table.HeadCell
                          className="py-2"
                          style={{ background: "#EBF5FF" }}
                        >
                          Pendências
                        </Table.HeadCell>
                      </Table.Head>

                      <Table.Body className="divide-y">
                        {Array.isArray(
                          data?.participacoes_socios_adm_outras_empresas?.socio
                            ?.socios_participantes
                        ) ? (
                          <>
                            {data?.participacoes_socios_adm_outras_empresas?.socio?.socios_participantes?.map(
                              (socio, index) => (
                                <Table.Row
                                  key={index}
                                  className="bg-white dark:border-gray-700 dark:bg-gray-800 text-black"
                                >
                                  <Table.Cell className="font-medium dark:text-white text-center py-1 px-1">
                                    {index + 1}
                                  </Table.Cell>
                                  <Table.Cell className=" py-1 w-96">
                                    <div>{socio.razao_social}</div>
                                    <div style={{ fontSize: "12px" }}>
                                      CNPJ: {socio.cnpj}
                                    </div>
                                  </Table.Cell>
                                  <Table.Cell className="text-center py-1 px-1">
                                    {socio.entrada}
                                  </Table.Cell>
                                  <Table.Cell className="text-center py-1 px-1 px-1">
                                    {socio.participacao}
                                  </Table.Cell>
                                  <Table.Cell className="text-center py-1 w-24">
                                    {socio.situacao_cnpj}
                                  </Table.Cell>
                                  <Table.Cell className="py-1 text-2xs text-black">
                                    {socio?.indicador_debito?.toUpperCase() ===
                                    "SIM" ? (
                                      <>
                                        {socio?.restricoes_documento
                                          ?.cheques_sem_fundo?.quantidade !==
                                          "0" && (
                                          <div className="uppercase">
                                            Cheques Sem Fundos:{" "}
                                            <span className="text-red-500 font-semibold">
                                              {
                                                socio?.restricoes_documento
                                                  ?.cheques_sem_fundo
                                                  ?.quantidade
                                              }{" "}
                                              ocorrências
                                            </span>
                                          </div>
                                        )}

                                        {socio?.restricoes_documento?.protestos
                                          ?.quantidade !== "0" && (
                                          <div className="uppercase">
                                            Protestos:{" "}
                                            <span className="text-red-500 font-semibold">
                                              {formatToReal(
                                                socio?.restricoes_documento
                                                  ?.protestos?.valor
                                              )}
                                            </span>
                                          </div>
                                        )}

                                        {socio?.restricoes_documento
                                          ?.pendencias_restricoes
                                          ?.quantidade !== "0" && (
                                          <div className="uppercase">
                                            Pendências:{" "}
                                            <span className="text-red-500 font-semibold">
                                              {formatToReal(
                                                socio?.restricoes_documento
                                                  ?.pendencias_restricoes?.valor
                                              )}
                                            </span>
                                          </div>
                                        )}

                                        {socio?.restricoes_documento
                                          ?.acoes_judiciais?.quantidade !==
                                          "0" && (
                                          <div className="uppercase">
                                            Ações cíveis:{" "}
                                            <span className="text-red-500 font-semibold">
                                              {formatToReal(
                                                socio?.restricoes_documento
                                                  ?.acoes_judiciais?.valor
                                              )}
                                            </span>
                                          </div>
                                        )}

                                        {socio?.restricoes_documento
                                          ?.falencias_recuperacao_judicial
                                          ?.quantidade !== "0" && (
                                          <div className="uppercase">
                                            Rec. Judicial/Falências:{" "}
                                            <span className="text-red-500 font-semibold">
                                              {formatToReal(
                                                socio?.restricoes_documento
                                                  ?.falencias_recuperacao_judicial
                                                  ?.valor
                                              )}
                                            </span>
                                          </div>
                                        )}
                                      </>
                                    ) : (
                                      <div className="text-blue-500">
                                        Não há débitos
                                      </div>
                                    )}
                                  </Table.Cell>
                                </Table.Row>
                              )
                            )}
                          </>
                        ) : (
                          <>
                            <Table.Row className="bg-white dark:border-gray-700 dark:bg-gray-800 text-black">
                              <Table.Cell className="font-medium dark:text-white text-center py-1 px-1">
                                {1}
                              </Table.Cell>
                              <Table.Cell className=" py-1 w-96">
                                <div>
                                  {
                                    data
                                      ?.participacoes_socios_adm_outras_empresas
                                      ?.socio?.socios_participantes
                                      ?.razao_social
                                  }
                                </div>
                                <div style={{ fontSize: "10px" }}>
                                  CNPJ:{" "}
                                  {
                                    data
                                      ?.participacoes_socios_adm_outras_empresas
                                      ?.socio?.socios_participantes?.cnpj
                                  }
                                </div>
                              </Table.Cell>
                              <Table.Cell className="text-center py-1 px-1">
                                {
                                  data?.participacoes_socios_adm_outras_empresas
                                    ?.socio?.socios_participantes?.entrada
                                }
                              </Table.Cell>
                              <Table.Cell className="text-center py-1 px-1 px-1">
                                {
                                  data?.participacoes_socios_adm_outras_empresas
                                    ?.socio?.socios_participantes?.participacao
                                }
                              </Table.Cell>
                              <Table.Cell className="text-center py-1 w-24">
                                {
                                  data?.participacoes_socios_adm_outras_empresas
                                    ?.socio?.socios_participantes?.situacao_cnpj
                                }
                              </Table.Cell>
                              <Table.Cell className="py-1 text-2xs text-black">
                                {data?.participacoes_socios_adm_outras_empresas?.socio?.socios_participantes.indicador_debito?.toUpperCase() ===
                                "SIM" ? (
                                  <>
                                    {data
                                      ?.participacoes_socios_adm_outras_empresas
                                      ?.socio?.socios_participantes
                                      .restricoes_documento?.cheques_sem_fundo
                                      ?.quantidade !== "0" && (
                                      <div className="uppercase">
                                        Cheques Sem Fundos:{" "}
                                        <span className="text-red-500 font-semibold">
                                          {
                                            data
                                              ?.participacoes_socios_adm_outras_empresas
                                              ?.socio?.socios_participantes
                                              .restricoes_documento
                                              ?.cheques_sem_fundo?.quantidade
                                          }
                                        </span>
                                      </div>
                                    )}

                                    {data
                                      ?.participacoes_socios_adm_outras_empresas
                                      ?.socio?.socios_participantes
                                      .restricoes_documento?.protestos
                                      ?.quantidade !== "0" && (
                                      <div className="uppercase">
                                        Protestos:{" "}
                                        <span className="text-red-500 font-semibold">
                                          {formatToReal(
                                            data
                                              ?.participacoes_socios_adm_outras_empresas
                                              ?.socio?.socios_participantes
                                              .restricoes_documento?.protestos
                                              ?.valor
                                          )}
                                        </span>
                                      </div>
                                    )}

                                    {data
                                      ?.participacoes_socios_adm_outras_empresas
                                      ?.socio?.socios_participantes
                                      .restricoes_documento
                                      ?.pendencias_restricoes?.quantidade !==
                                      "0" && (
                                      <div className="uppercase">
                                        Pendências:{" "}
                                        <span className="text-red-500 font-semibold">
                                          {formatToReal(
                                            data
                                              ?.participacoes_socios_adm_outras_empresas
                                              ?.socio?.socios_participantes
                                              .restricoes_documento
                                              ?.pendencias_restricoes?.valor
                                          )}
                                        </span>
                                      </div>
                                    )}

                                    {data
                                      ?.participacoes_socios_adm_outras_empresas
                                      ?.socio?.socios_participantes
                                      .restricoes_documento?.acoes_judiciais
                                      ?.quantidade !== "0" && (
                                      <div className="uppercase">
                                        Ações cíveis:{" "}
                                        <span className="text-red-500 font-semibold">
                                          {formatToReal(
                                            data
                                              ?.participacoes_socios_adm_outras_empresas
                                              ?.socio?.socios_participantes
                                              .restricoes_documento
                                              ?.acoes_judiciais?.valor
                                          )}
                                        </span>
                                      </div>
                                    )}

                                    {data
                                      ?.participacoes_socios_adm_outras_empresas
                                      ?.socio?.socios_participantes
                                      .restricoes_documento
                                      ?.falencias_recuperacao_judicial
                                      ?.quantidade !== "0" && (
                                      <div className="uppercase">
                                        Rec. Judicial/Falências:{" "}
                                        <span className="text-red-500 font-semibold">
                                          {formatToReal(
                                            data
                                              ?.participacoes_socios_adm_outras_empresas
                                              ?.socio?.socios_participantes
                                              .restricoes_documento
                                              ?.falencias_recuperacao_judicial
                                              ?.valor
                                          )}
                                        </span>
                                      </div>
                                    )}
                                  </>
                                ) : (
                                  <div className="text-blue-500">
                                    Não há débitos
                                  </div>
                                )}
                              </Table.Cell>
                            </Table.Row>
                          </>
                        )}
                      </Table.Body>
                    </Table>
                  </div>
                </div>
              </>
            )}
          </>
        ) : (
          <NoOcurrence />
        )}
      </div>

      <div>
        <TitleStripe className="mb-5">Cheques Sem Fundos</TitleStripe>
        {!!data?.cheques_sem_fundo?.total_cheques_sem_fundos ? (
          <>
            <ChequesSemFundos
              quantidadeTotal={
                data?.cheques_sem_fundo?.total_cheques_sem_fundos
              }
              totalNomes={data?.cheques_sem_fundo?.total_nomes_relacionados}
              chequesSemFundos={
                Array.isArray(
                  data?.cheques_sem_fundo?.ultimas_ocorrencias?.cheque_sem_fundo
                )
                  ? data?.cheques_sem_fundo?.ultimas_ocorrencias
                      ?.cheque_sem_fundo
                  : [
                      ...[],
                      data?.cheques_sem_fundo?.ultimas_ocorrencias
                        ?.cheque_sem_fundo,
                    ]
              }
            />
          </>
        ) : (
          <NoOcurrence />
        )}
      </div>
      <div>
        <TitleStripe className="mb-5">Cheques Sustados</TitleStripe>
        {data?.cheques_sustados?.ultimas_ocorrencias?.cheques_sustado?.length >
        0 ? (
          <>
            <ChequesSustados
              periodoInicial={data?.cheques_sustados?.periodo_inicial}
              periodoFinal={data?.cheques_sustados?.periodo_final}
              quantidadeTotal={data?.cheques_sustados?.total}
              chequesSustados={
                Array.isArray(
                  data?.cheques_sustados?.ultimas_ocorrencias?.cheques_sustado
                )
                  ? data?.cheques_sustados?.ultimas_ocorrencias?.cheques_sustado
                  : [
                      ...[],
                      data?.cheques_sustados?.ultimas_ocorrencias
                        ?.cheques_sustado,
                    ]
              }
            />
          </>
        ) : (
          <NoOcurrence />
        )}
      </div>

      <div>
        <TitleStripe className="mb-5">Protestos</TitleStripe>
        {!!data?.protestos?.total_protestos ? (
          <Protestos
            quantidadeTotal={data?.protestos?.total_protestos}
            valorTotal={data?.protestos?.valor_total}
            primeiroValor={data?.protestos?.valor_primeiro}
            protestos={
              Array.isArray(data?.protestos?.ultimas_ocorrencias?.protesto)
                ? data?.protestos?.ultimas_ocorrencias?.protesto
                : [...[], data?.protestos?.ultimas_ocorrencias?.protesto]
            }
            temVencimento={false}
          />
        ) : (
          <NoOcurrence />
        )}
      </div>

      <div>
        <TitleStripe className="mb-5">Negativações Registradas</TitleStripe>
        {data?.pendencias_restricoes?.ultimas_ocorrencias
          ?.pendencia_restricao ? (
          <PendenciasFinanceiras
            quantidadeTotal={data?.pendencias_restricoes?.total_pendencias}
            dataMaiorPendencia={data?.pendencias_restricoes?.data_maior}
            valorTotal={data?.pendencias_restricoes?.valor}
            pendenciasFinanceiras={(Array.isArray(
              data?.pendencias_restricoes?.ultimas_ocorrencias
                ?.pendencia_restricao
            )
              ? data?.pendencias_restricoes?.ultimas_ocorrencias
                  ?.pendencia_restricao
              : [
                  data?.pendencias_restricoes?.ultimas_ocorrencias
                    ?.pendencia_restricao,
                ]
            ).map((item) => ({
              data: item?.data_debito || null,
              valor: item?.valor || 0,
              informante: item?.informante || "",
              tipo: item?.tipo || "",
              documento: item?.documento || "",
              situacao: item?.situacao || "",
              uf: item?.uf || "",
              cidade: item?.cidade || "",
            }))}
          />
        ) : (
          <NoOcurrence />
        )}
      </div>

      <div>
        <TitleStripe className="mb-5">Ações Cíveis</TitleStripe>
        {!!data?.acoes_civeis?.total_acoes ? (
          <>
            <AcoesCiveis
              quantidadeTotal={data?.acoes_civeis?.total_acoes}
              primeiroValor={data?.acoes_civeis?.valor_primeiro}
              valorTotal={data?.acoes_civeis?.valor_total}
              acoesCiveis={
                Array.isArray(
                  data?.acoes_civeis?.ultimas_ocorrencias?.acao_civel
                )
                  ? data?.acoes_civeis?.ultimas_ocorrencias?.acao_civel
                  : [...[], data?.acoes_civeis?.ultimas_ocorrencias?.acao_civel]
              }
            />
          </>
        ) : (
          <NoOcurrence />
        )}
      </div>

      <div>
        <TitleStripe className="mb-5">
          Falências/Recuperações Judiciais
        </TitleStripe>
        {data && !data?.falencias_recuperacao_judicial?.mensagem ? (
          <FalenciaRecuperacaoJudicial
            falenciaRecuperacaoJudicial={
              Array.isArray(
                data?.falencias_recuperacao_judicial?.ultimas_ocorrencias
                  ?.falencia_recuperacao_judicial
              )
                ? data?.falencias_recuperacao_judicial?.ultimas_ocorrencias
                    ?.falencia_recuperacao_judicial
                : [
                    ...[],
                    data?.falencias_recuperacao_judicial?.ultimas_ocorrencias
                      ?.falencia_recuperacao_judicial,
                  ]
            }
          />
        ) : (
          <NoOcurrence />
        )}
      </div>

      <div>
        <TitleStripe className="mb-5">Histórico de Consultas</TitleStripe>

        {data?.consultas?.ultimas_consultas?.consulta?.length > 0 ? (
          <>
            <HistoricoConsultas
              quantidadeTotal={data?.consultas?.quantidade_total}
              periodoInicial={data?.consultas?.periodo_inicial}
              periodoFinal={data?.consultas?.periodo_final}
              historicoConsultas={
                Array.isArray(data?.consultas?.ultimas_consultas?.consulta)
                  ? data?.consultas?.ultimas_consultas?.consulta?.map(
                      (hist) => ({
                        data: hist.data,
                        empresa: hist.razao_social,
                      })
                    )
                  : [
                      ...[],
                      {
                        data: data?.consultas?.ultimas_consultas?.consulta
                          ?.data,
                        empresa:
                          data?.consultas?.ultimas_consultas?.consulta
                            ?.razao_social,
                      },
                    ]
              }
            />
          </>
        ) : (
          <NoOcurrence />
        )}

        <div className="border border-gray-300 rounded-lg mt-3">
          <Table striped>
            <Table.Head>
              {data?.consultas?.consultas_mes?.map((consulta, index) => (
                <Table.HeadCell
                  key={index}
                  className="text-center text-black px-1"
                  style={{ background: "#EBF5FF" }}
                >
                  {formatDateToDDMM(consulta.mes_ano)}
                </Table.HeadCell>
              ))}
            </Table.Head>

            <Table.Body className="divide-y">
              <Table.Row className="bg-white dark:border-gray-700 dark:bg-gray-800">
                {data?.consultas?.consultas_mes?.map((consult, index) => (
                  <Table.Cell
                    className="font-medium text-gray-900 dark:text-white text-center text-black py-1 px-1"
                    key={index}
                  >
                    {consult.quantidade}
                  </Table.Cell>
                ))}
              </Table.Row>
            </Table.Body>
          </Table>
        </div>
      </div>
    </div>
  );
};

export default DefineLimiteQuery;
